import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import ChallengeEntry from "../models/ChallengeEntry";
import { useAuth } from "../auth/AuthProvider";
import "react-datepicker/dist/react-datepicker.css";

const ChallengeRegistrationForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialChallenge = searchParams.get('challenge') || "";
  const initialDate = searchParams.get('date')
    ? new Date(searchParams.get('date') + 'T12:00:00')
    : new Date();

  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(initialChallenge);
  const [count, setCount] = useState(10);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      ChallengeEntry.fetchChallenges(user.uid).then((data) => setChallenges(data));
    }
  }, [user]);

  const getSelectedChallengeData = () => {
    return challenges.find(c => c.id === selectedChallenge);
  };

  const getQuickAddValues = () => {
    const goal = challengeData?.goal || 100;

    if (goal <= 15) {
      return [1, 5, 10];
    } else if (goal <= 30) {
      return [5, 10, 15];
    } else if (goal <= 60) {
      return [10, 15, 30];
    } else {
      return [10, 25, 50];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedChallenge) {
      alert("Please select a challenge!");
      return;
    }

    setLoading(true);
    const formattedDate = selectedDate.toISOString().split("T")[0];

    try {
      const entry = new ChallengeEntry(user.uid, selectedChallenge, formattedDate, count);
      await ChallengeEntry.save(entry);

      const challengeData = getSelectedChallengeData();
      const unit = challengeData?.goalType === 'minutes' ? ' minutes' : '';

      setSuccessMessage(
        `Added ${count}${unit} to ${challengeData?.originalName || selectedChallenge}`
      );
      setCount(10);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const challengeData = getSelectedChallengeData();
  const unit = challengeData?.goalType === 'minutes' ? 'min' : '';

  return (
    <div className="max-w-md md:max-w-xl lg:max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 md:p-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Add Entry
        </h1>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-lg flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Challenge Select */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Challenge
            </label>
            <select
              value={selectedChallenge}
              onChange={(e) => setSelectedChallenge(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base"
              required
            >
              <option value="">Select a challenge</option>
              {challenges.map((challenge) => (
                <option key={challenge.id} value={challenge.id}>
                  {challenge.originalName}
                </option>
              ))}
            </select>
          </div>

          {/* Count Input */}
          <div className="mb-5">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Amount {unit && `(${unit})`}
              </label>
              <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {count}
              </span>
            </div>

            {/* Slider or Input based on whether count exceeds goal */}
            {!challengeData || count <= challengeData.goal ? (
              <input
                type="range"
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value, 10))}
                min={1}
                max={challengeData?.goal || 100}
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
            ) : (
              <input
                type="number"
                value={count}
                onChange={(e) => setCount(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-lg font-semibold focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                min="1"
              />
            )}

            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>1</span>
              <span>Goal: {challengeData?.goal || 100}</span>
            </div>
          </div>

          {/* Quick add buttons - dynamic based on goal */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Quick add
            </label>
            <div className="grid grid-cols-4 gap-2">
              {getQuickAddValues().map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setCount(count + num)}
                  className="py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
                >
                  +{num}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCount(1)}
                className="py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Date Picker with arrows */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() - 1);
                  setSelectedDate(newDate);
                }}
                className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex-1 text-center">
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  dateFormat="EEE, d MMM yyyy"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-base focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  maxDate={new Date()}
                  popperPlacement="top"
                  wrapperClassName="w-full"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() + 1);
                  if (newDate <= new Date()) {
                    setSelectedDate(newDate);
                  }
                }}
                disabled={selectedDate.toDateString() === new Date().toDateString()}
                className="flex-shrink-0 p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading || !selectedChallenge}
              className="w-1/2 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded font-medium transition-colors"
            >
              {loading ? 'Saving...' : 'Save Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChallengeRegistrationForm;
